import { useState, useEffect } from 'react'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { useIsMobile } from '../hooks/useIsMobile'
import { useSeccionesPlataforma } from '../hooks/useSeccionesPlataforma'
import { construirMenu, entradaActiva, RUTA_INICIO } from '../services/menuPlataforma'
import { Ico } from './ui'
import MarcaBiango, { MARCA_NOMBRE } from './MarcaBiango'
import ErrorBoundary from './ErrorBoundary'
import DentroDeClinica from './DentroDeClinica'
import AvatarPorFirmar from './AvatarPorFirmar'
// B-361 · la IA del equipo de soporte, a mano en todo el panel (solo con la sección de soporte).
import ConsultaIASoporte from './ConsultaIASoporte'
import { tituloPestana } from '../utils/siglas'
// t478 · el número de consultas de soporte que esperan, en «Consultas» del menú y arriba (lo anuncia AvisoSoporteNuevo).
import { escucharSoportePendientes, soportePendientesAhora } from '../utils/soportePendientes'
// t478 · varias cuentas en la misma app (como Gmail): pasar a la de la clínica, o «Añadir otra cuenta», desde el panel.
import { MenuDeCuentas } from '../utils/MenuDeCuentas'

// ══════════════════════════════════════════════════════════════════════════════════════════════
//  EL CHROME DEL PANEL DE PLATAFORMA (su propio menú, no el de la clínica)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  DE DÓNDE SALE. Estando dentro de /superadmin, el menú lateral seguía siendo el de la clínica —
//  pacientes, agenda, cobros, almacén— y la pantalla era un scroll de diez paneles apilados. Se
//  trabaja aquí para dar de alta clínicas, emitir los tokens de su conector y editar el texto legal
//  que firman los pacientes de TODAS: nada de eso tiene que ver con el menú que había al lado.
//
//  SIGUE EL PATRÓN DE LA CASA, no inventa un tercero. Es el mismo esqueleto que AdminLayout.jsx: el
//  mismo aside de 256 px con la marca arriba y cabeceras de grupo en versalitas, la misma forma de
//  marcar la sección activa (fondo `color-mix` del color de marca al 14 % y el texto en `--primary`),
//  los mismos iconos de `ui.jsx`, el mismo botón de color abajo para salir del "mundo panel" y el
//  mismo cajón con velo en pantalla pequeña.
//
//  ── LAS TRES FORMAS, Y POR QUÉ TRES ──────────────────────────────────────────────────────────
//  `data-menu` dice cuál está puesta (se pinta en el HTML a propósito: es lo que mide la guardia).
//   · `fijo`  (>= 1100 px, ordenador): aside de 256 px siempre a la vista, con los nombres. No hay
//     botón de menú, porque no hay nada que abrir.
//   · `rail`  (720–1099 px, TABLET): un carril de 76 px con SOLO los iconos, siempre a la vista. Un
//      aside de 256 px se come un tercio de una tablet en vertical; y esconderlo del todo, como en el
//      móvil, obliga a abrir un cajón para cada salto teniendo sitio de sobra para no hacerlo. El
//      botón de menú lo despliega encima del contenido cuando hace falta leer los nombres.
//   · `cajon` (< 720 px, móvil): fuera de la pantalla hasta que se pulsa el botón; entonces entra
//      con velo por encima, y se cierra solo al navegar.
//  Los dos cortes se leen con el `useIsMobile` que ya existe (dos umbrales, no un hook nuevo).
//
//  ── EL MENÚ SOLO ENSEÑA LO QUE ESA PERSONA PUEDE ─────────────────────────────────────────────
//  Las entradas salen de `services/menuPlataforma.js` cruzando el catálogo del servidor con las
//  secciones concedidas. Una sección a la que no se entra NO SE PINTA: enseñar una puerta que da 403
//  solo consigue que alguien pulse, no entienda por qué no puede y de paso se entere de que existe.
//  Y esconder la entrada NO es cerrar la puerta: la ruta directa la cierra `SeccionPlataforma`
//  (components/PlataformaPuertas.jsx), y detrás de todo está el servidor, que es el único que de
//  verdad decide. El aterrizaje de /superadmin a secas lo calcula esa misma pieza, con ESTE menú.
// ══════════════════════════════════════════════════════════════════════════════════════════════

// Los dos cortes. Escritos aquí como constantes con nombre para que la guardia pueda pintar justo a
// los dos lados de cada uno y no a ojo.
export const CORTE_MOVIL = 720
export const CORTE_TABLET = 1100

const inicial = (n) => (n || '?').trim()[0]?.toUpperCase() || '?'

// EL AVISO DEL DISCO DEL SERVIDOR (2-oct-2026), CARGADO APARTE con un import dinámico: el panel no espera por él (va en su
// propio trozo) y, si no se puede cargar, no se pinta nada. Sus hooks viven en este componente pequeño, no en el layout.
const cargarAvisoDisco = () => import('./AvisoDiscoServidor')
function AvisoDiscoPerezoso({ soloSiAvisa = false }) {
  const [Aviso, setAviso] = useState(null)
  useEffect(() => {
    let vivo = true
    cargarAvisoDisco().then((m) => { if (vivo) setAviso(() => m.default) }).catch(() => {})
    return () => { vivo = false }
  }, [])
  return Aviso ? <Aviso soloSiAvisa={soloSiAvisa} /> : null
}

// EL RELÉ POR DETRÁS (1-nov-2026, t621), igual: cargado aparte y, si no se puede cargar, no se pinta nada.
const cargarAvisoRele = () => import('./AvisoRele')
function AvisoRelePerezoso() {
  const [Aviso, setAviso] = useState(null)
  useEffect(() => {
    let vivo = true
    cargarAvisoRele().then((m) => { if (vivo) setAviso(() => m.default) }).catch(() => {})
    return () => { vivo = false }
  }, [])
  return Aviso ? <Aviso /> : null
}

// «PASAR A PRODUCCIÓN ESTA NOCHE» (3-oct-2026), cargado aparte como el aviso del disco. El bloque solo pregunta al
// servidor y solo se pinta si las pantallas se compilaron para el entorno de pruebas (VITE_ENTORNO=pruebas): en
// producción no hace nada. (La comprobación vive dentro del trozo: este layout no puede leer import.meta.)
const cargarPasoProduccion = () => import('./PasoProduccionPruebas')
function PasoProduccionPerezoso() {
  const [Bloque, setBloque] = useState(null)
  useEffect(() => {
    let vivo = true
    cargarPasoProduccion().then((m) => { if (vivo) setBloque(() => m.default) }).catch(() => {})
    return () => { vivo = false }
  }, [])
  return Bloque ? <Bloque /> : null
}

// EL SEGUNDO FACTOR DE LA CUENTA DE BIANGO (3-oct-2026), cargado aparte como los dos de arriba: pide el código de la app
// cuando el servidor lo vuelve a pedir («Entrar en esta clínica») y, mientras no es obligatorio, ofrece activarlo.
const cargarSegundoFactor = () => import('./SegundoFactorPanel')
function SegundoFactorPerezoso() {
  const [Bloque, setBloque] = useState(null)
  useEffect(() => {
    let vivo = true
    cargarSegundoFactor().then((m) => { if (vivo) setBloque(() => m.default) }).catch(() => {})
    return () => { vivo = false }
  }, [])
  return Bloque ? <Bloque /> : null
}

// t251 (15-oct-2026) · EL AVISO LLAMATIVO DEL SOPORTE (components/AvisoSoporteNuevo), cargado aparte como los de arriba: una
// tarjeta fija hasta abrir la consulta, con sonido y el número en el título de la pestaña. Solo con la sección de soporte (el
// servidor la vuelve a mirar: sin ella contesta 403 y no se pinta nada).
const cargarAvisoSoporte = () => import('./AvisoSoporteNuevo')
// t478 · y el aviso al navegador, también con el panel cerrado: hooks/useNotifications · apuntarEsteNavegadorALosAvisos.
const cargarAvisosDelNavegador = () => import('../hooks/useNotifications')
// t478 · «Tus avisos de soporte» (components/MisAvisosSoporte): lo que elige cada persona del equipo y «Probar aviso», encima
// de Consultas. Aparte, como los avisos: si no se puede cargar, Consultas sigue igual.
const cargarMisAvisos = () => import('./MisAvisosSoporte')
function MisAvisosPerezoso({ ver = false }) {
  const [Bloque, setBloque] = useState(null)
  useEffect(() => {
    if (!ver) return undefined
    let vivo = true
    cargarMisAvisos().then((m) => { if (vivo) setBloque(() => m.default) }).catch(() => {})
    return () => { vivo = false }
  }, [ver])
  return ver && Bloque ? <Bloque /> : null
}
function AvisoSoportePerezoso() {
  const [Aviso, setAviso] = useState(null)
  useEffect(() => {
    let vivo = true
    cargarAvisoSoporte().then((m) => { if (vivo) setAviso(() => m.default) }).catch(() => {})
    return () => { vivo = false }
  }, [])
  return Aviso ? <Aviso /> : null
}

// B-1251 (12-oct-2026) · UNA SECCIÓN QUE VA DENTRO DE OTRA (el servidor lo dice con `menuDentroDe`: Precios de WhatsApp va dentro
// de WhatsApp). En la de fuera, su botón arriba a la derecha; en la de dentro, la vuelta. Sin pestañas: se entra y se vuelve. Lo
// pinta el layout para que ninguna pantalla tenga que saberlo. Componente aparte (y no un trozo del layout) para que lo que se
// mide del <main> (entrar-en-clinica §8) no dependa de las variables del layout.
export function BarraDeDentro({ actual, pathname, ir }) {
  if (!actual) return null
  const p = String(pathname || '')
  const lista = Array.isArray(actual.dentro) ? actual.dentro : []
  // Dentro: en una de sus páginas de dentro o, si la sección lleva la vuelta en cualquier dirección de debajo (la Biblioteca,
  // un tipo), en cualquiera de ellas.
  const dentro = lista.some(d => p === d.to || p.startsWith(d.to + '/'))
    || (actual.vueltaDesdeDentro === true && p.startsWith(actual.to + '/'))
  // Los botones de ir dentro, solo en la pantalla de la sección: en otra dirección de debajo que no es página de dentro (la de
  // una migración, /superadmin/migraciones/clinica/5, con su propio «Migraciones» arriba) no sale nada.
  if (!dentro && (!lista.length || p !== actual.to)) return null
  return (
    <div data-plataforma-dentro={actual.clave} style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: dentro ? 'flex-start' : 'flex-end', alignItems: 'center', maxWidth: '1150px', margin: '0 auto 12px' }}>
      {dentro
        ? <button type="button" data-plataforma-volver={actual.to} onClick={() => ir(actual.to)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', border: 'none', background: 'transparent', color: 'var(--enlace, var(--primary))', fontWeight: 700, fontSize: '13px', cursor: 'pointer', padding: '4px 0' }}>
            <Ico n="chevronL" s={16} />{actual.label}
          </button>
        : lista.map(d => (
            <button key={d.clave || d.to} type="button" data-plataforma-ir-dentro={d.to} onClick={() => ir(d.to)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-primary)', fontWeight: 600, fontSize: '13px', cursor: 'pointer', padding: '8px 14px', borderRadius: '9px' }}>
              {d.label}<Ico n="chevronR" s={15} />
            </button>
          ))}
    </div>
  )
}

export default function PlataformaLayout() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  useTheme()

  const esMovil = useIsMobile(CORTE_MOVIL)
  const esCompacto = useIsMobile(CORTE_TABLET)
  const modo = esMovil ? 'cajon' : (esCompacto ? 'rail' : 'fijo')

  const [abierto, setAbierto] = useState(false)
  // Al navegar se cierra: en móvil el cajón tapa la pantalla y en tablet el desplegable tapa el
  // contenido al que se acaba de ir.
  useEffect(() => { setAbierto(false) }, [location.pathname])

  const { secciones, catalogo, categorias, pleno, motivo, degradado, lecturaPanel, listo } = useSeccionesPlataforma()
  // EN AVERIA EL MENU SE PINTA ENTERO Y DE SOLO LECTURA. El servidor ya no concede secciones durante
  // una averia (`secciones` viene vacia), asi que sin esto el panel se quedaria en blanco justo
  // cuando hace falta mirarlo. Lo que se pinta son pantallas que contestan 200 a un GET; lo que
  // escribe sigue apagado, porque cada boton mira `pleno`/`secciones`.
  const menu = construirMenu({ catalogo, categorias, secciones, lecturaDeDiagnostico: degradado && lecturaPanel })
  const entradas = menu.filter(m => m.tipo === 'entrada')
  // Las cabeceras de grupo que se quedan sin nada debajo ya las quita `construirMenu`.

  // B-1251 · la entrada encendida: la de esta dirección, la de una página de dentro («/superadmin/biblioteca/medicamentos»)
  // o la de una sección que va dentro de ella (Precios de WhatsApp enciende WhatsApp). La regla vive en menuPlataforma.
  const activa = (m) => entradaActiva(m, location.pathname)
  const actual = entradas.find(activa)

  useEffect(() => {
    // La MISMA pestaña del navegador que en los otros dos layouts: el producto arriba y la sección
    // detrás. Sin esto se queda el título de la última pantalla de la clínica que se visitó.
    document.title = tituloPestana(MARCA_NOMBRE, actual ? actual.label : 'Plataforma')
  }, [location.pathname, actual?.label])

  // t478 (26-oct-2026) · CON UNA CUENTA SOLO DE PLATAFORMA HAY QUE ENTERARSE IGUAL. El número de consultas de soporte que
  // esperan (utils/soportePendientes: lo anuncia el aviso de abajo, que pregunta al servidor), en el menú y en la cabecera.
  const [soporte, setSoporte] = useState(() => soportePendientesAhora())
  useEffect(() => escucharSoportePendientes(setSoporte), [])
  // Y ESTE NAVEGADOR SE APUNTA A LOS AVISOS, como en la aplicación de la clínica: sin esto, con el panel cerrado no llegaba
  // nada al ordenador. Aparte (import dinámico), como los avisos de abajo: si no se puede cargar, el panel sigue igual.
  useEffect(() => {
    let vivo = true
    cargarAvisosDelNavegador().then((m) => { if (vivo && m && typeof m.apuntarEsteNavegadorALosAvisos === 'function') m.apuntarEsteNavegadorALosAvisos() }).catch(() => {})
    return () => { vivo = false }
  }, [])

  // EL MODO AVERIA TIENE QUE DECIRSE, y decir lo que de verdad pasa. Aquí se calculaba con
  // `motivo === 'sin_migrar' || motivo === 'sin_pleno_degradado'` —la lista de averías que había el
  // día que se escribió— y el aviso decía «todo el personal de plataforma lo ve todo», que hoy es
  // falso por los dos lados: ni se abren los datos de las clínicas, ni se puede ESCRIBIR nada. Ahora
  // el hecho lo manda el servidor (`degradado`) y el texto está abajo, junto al recuadro.

  // Los nombres solo se pintan cuando hay sitio: en el carril de la tablet estorban, y ahí el nombre
  // viaja en el `title` de cada entrada.
  const conNombres = modo !== 'rail' || abierto
  const comoCajon = modo === 'cajon' || (modo === 'rail' && abierto)
  const anchoAside = modo === 'fijo' ? '256px' : (comoCajon ? '260px' : '76px')

  const asideStyle = {
    width: anchoAside, flexShrink: 0, background: 'var(--surface)', borderRight: '1px solid var(--border)',
    display: 'flex', flexDirection: 'column',
    // El final del menú llega al borde de abajo: en Android, debajo de la barra del sistema (index.css, --sa-abajo).
    paddingBottom: 'var(--sa-abajo)',
    ...(comoCajon ? {
      position: 'fixed', top: 0, bottom: 0, left: 0, zIndex: 1200, maxWidth: 'calc(82vw / var(--z, 1))',
      transform: (modo === 'cajon' && !abierto) ? 'translateX(-100%)' : 'translateX(0)',
      transition: 'transform .25s ease',
      boxShadow: abierto ? '0 10px 40px rgba(0,0,0,0.3)' : 'none',
    } : {}),
  }

  const nombre = `${user?.nombre || ''} ${user?.apellidos || ''}`.trim() || 'Usuario'

  return (
    <div data-plataforma-layout data-menu={modo} style={{ display: 'flex', height: 'var(--alto-pantalla)', overflow: 'hidden', background: 'var(--bg)', color: 'var(--text-primary)' }}>
      {comoCajon && abierto && (
        <div onClick={() => setAbierto(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1100 }} />
      )}

      <aside data-plataforma-menu={modo} style={asideStyle}>
        {/* iPhone/notch: empezar debajo del safe-area, igual que en los otros dos layouts. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: `calc(18px + env(safe-area-inset-top, 0px)) ${conNombres ? '20px' : '14px'} 18px`, borderBottom: '1px solid var(--border-light)' }}>
          <MarcaBiango size={conNombres ? 38 : 34} radio={11} />
          {conNombres && (
            <span style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: '14.5px', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{MARCA_NOMBRE}</div>
              <div style={{ display: 'inline-flex', alignItems: 'center', fontSize: '10px', fontWeight: 800, letterSpacing: '.5px', textTransform: 'uppercase', color: 'var(--primary)', background: 'color-mix(in srgb, var(--primary) 12%, transparent)', borderRadius: '5px', padding: '1px 7px', marginTop: '3px' }}>Plataforma</div>
            </span>
          )}
        </div>

        <nav style={{ flex: 1, overflowY: 'auto', padding: conNombres ? '14px 12px' : '14px 10px' }}>
          {/* MIENTRAS NO SE SABE, NO SE DICE NADA. Pintar "no tienes ninguna sección" durante el
              medio segundo que tarda la respuesta es mentirle a quien las tiene todas. */}
          {!listo && <div style={{ padding: '10px 12px', fontSize: '12.5px', color: 'var(--text-muted)' }}>{conNombres ? 'Cargando el menú…' : '…'}</div>}
          {listo && entradas.length === 0 && conNombres && (
            <div style={{ padding: '12px', fontSize: '12.5px', color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Tu cuenta es de plataforma pero todavía no tiene ninguna sección concedida. Pídesela a
              quien lleve el equipo de plataforma.
            </div>
          )}
          {listo && menu.map(m => m.tipo === 'grupo' ? (
            conNombres
              ? <div key={'g:' + m.clave} style={{ fontSize: '10.5px', fontWeight: 800, letterSpacing: '.6px', color: 'var(--text-muted)', textTransform: 'uppercase', padding: '14px 12px 5px' }}>{m.label}</div>
              // En el carril no caben las versalitas: el grupo se marca con una raya, que separa
              // igual y no ocupa. Sin nada, los nueve iconos se leerían como una sola lista.
              : <div key={'g:' + m.clave} title={m.label} style={{ height: '1px', background: 'var(--border)', margin: '12px 6px 8px' }} />
          ) : (
            <NavLink key={m.clave} to={m.to} data-seccion={m.clave} title={m.labelLargo}
              style={{
                display: 'flex', alignItems: 'center', gap: '12px', padding: conNombres ? '11px 13px' : '11px 0',
                justifyContent: conNombres ? 'flex-start' : 'center',
                borderRadius: '11px', marginBottom: '4px', textDecoration: 'none', fontSize: '14px', fontWeight: 600,
                background: activa(m) ? 'color-mix(in srgb, var(--primary) 14%, transparent)' : 'transparent',
                color: activa(m) ? 'var(--primary)' : 'var(--text-secondary)',
              }}>
              <Ico n={m.icono} s={19} c={activa(m) ? 'var(--primary)' : 'var(--text-muted)'} />
              {conNombres && <span style={{ flex: 1, minWidth: 0 }}>{m.label}</span>}
              {/* t478 · las consultas de soporte que esperan con algo sin leer: el número, en rojo, en su entrada del menú. */}
              {m.clave === 'soporte' && soporte.n > 0 && (
                <span data-soporte-pendientes-menu={soporte.n} title={soporte.n === 1 ? '1 consulta espera respuesta' : soporte.n + ' consultas esperan respuesta'}
                  style={{ minWidth: '20px', height: '20px', padding: '0 6px', borderRadius: '999px', background: 'var(--danger)', color: '#fff', fontSize: '11.5px', fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {soporte.n > 99 ? '99+' : soporte.n}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div style={{ padding: conNombres ? '14px 14px 18px' : '12px 10px 16px' }}>
          {/* EN EL CARRIL DE LA TABLET (720–1099 px) NO CABE EL RECUADRO, PERO EL AVISO NO PUEDE
              DESAPARECER. Aqui solo estaba el bloque de abajo, colgado de `conNombres`: en ese
              tramo el menu se pintaba con sus diez entradas y no habia ni una palabra que dijera
              que el panel esta averiado, asi que cada guardado contestaba 503 sin explicacion. La
              marca del carril lleva el motivo en el `title`; el detalle de cual de las tres
              averias es se lee al desplegar, que es donde vive escrito UNA sola vez. */}
          {degradado && !conNombres && (
            <div data-averia-carril title="El panel esta en modo averia: solo lectura. Abre el menu para ver el motivo."
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface-2)', border: '1px solid var(--warning)', borderRadius: '11px', padding: '9px 0', marginBottom: '10px' }}>
              <Ico n="warn" s={18} c="var(--warning)" />
            </div>
          )}
          {/* EL «ENTERO» DE `sin_migrar` LLEVA SU SALVEDAD, Y NO ES UN ADORNO. Aquí ponía «se puede
              mirar el panel entero para diagnosticar» a secas — palabra por palabra la promesa que
              se declaró falsa y por la que se escribió la migración 2027-04-16 y se corrigió
              EquipoPlataforma.jsx. Y como /superadmin/equipo se pinta DENTRO de este layout, la
              MISMA pantalla decía «se puede mirar el panel entero» en el carril y «no se sabe» en
              el cuerpo: A y no-A a la vez, delante de la única persona que puede arreglar la
              avería. El hecho es que la migración que falta es la que crea `agentes.plataforma_pleno`
              y `plataforma_permisos`, así que en ESTA avería —y solo en esta, ver §2c de la
              guardia— el reparto no se sabe. Lo mide, pintando este recuadro y no leyendo el
              fichero, routes/plataforma-averia-se-puede-leer.test.js §6. */}
          {degradado && conNombres && (
            <div data-averia-recuadro style={{ background: 'var(--surface-2)', border: '1px solid var(--warning)', borderRadius: '12px', padding: '11px 12px', marginBottom: '10px', fontSize: '11.5px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              <b>El panel está en modo avería: solo lectura.</b>{' '}
              {motivo === 'sin_migrar'
                ? 'Faltan las migraciones de permisos de plataforma. Hasta que se apliquen se puede mirar el panel entero para diagnosticar, y no se concede ninguna sección: no se puede cambiar nada, ni aquí ni fuera del panel. Entero con una salvedad, y es justo esta avería: la migración que falta es la que crea dónde se guarda el acceso pleno, así que hasta aplicarla no se sabe quién tiene el acceso pleno ni qué secciones tiene cada uno. Quién entra al panel sí se lee.'
                : motivo === 'sin_saber'
                  ? 'No se ha podido comprobar si queda alguna cuenta con acceso pleno: la base de datos no ha contestado. Mientras no se sepa se asume lo peor, así que se puede mirar el panel y no se puede cambiar nada. Vuelve a intentarlo en unos segundos.'
                  : 'No queda ninguna cuenta con acceso pleno. Se puede mirar el panel entero para ver qué pasa, y no se concede ninguna sección mientras dura: no se puede cambiar nada, ni aquí ni fuera del panel. Se arregla dándole el acceso pleno a una persona desde el servidor.'}
            </div>
          )}
          {/* SALIR DEL "MUNDO PLATAFORMA". Mismo botón de color que en el panel de administración: se
              entra desde el menú de la foto y sin esto la única salida era teclear la URL. */}
          {/* t478 · las otras cuentas abiertas en este dispositivo (la de tu clínica) y «Añadir otra cuenta». */}
          {conNombres && <MenuDeCuentas onAntes={() => setAbierto(false)} />}
          {/* t478 · una cuenta SOLO de plataforma entra al panel desde la raíz (services/home · esCuentaSoloDePlataforma): para
              ella, «/» volvería aquí mismo. Va a la página de inicio de su aplicación. */}
          <button onClick={() => navigate(user?.cuenta_de_plataforma === true ? '/inicio' : '/')} title="Salir del panel de plataforma y volver a la aplicación de la clínica"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '9px', width: '100%', border: 'none',
              background: 'var(--primary)', color: 'var(--sobre-primary)', fontSize: '13.5px', fontWeight: 800, cursor: 'pointer', padding: '12px', borderRadius: '11px',
              boxShadow: '0 2px 10px color-mix(in srgb, var(--primary) 38%, transparent)' }}>
            <Ico n="inicio" s={17} c="var(--sobre-primary)" />{conNombres && <span>Volver a la clínica</span>}
          </button>
        </div>
      </aside>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <header style={{ height: 'calc(62px + env(safe-area-inset-top, 0px))', flexShrink: 0, background: 'var(--surface)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '12px', padding: esMovil ? 'env(safe-area-inset-top, 0px) 14px 0' : 'env(safe-area-inset-top, 0px) 24px 0' }}>
          {modo !== 'fijo' && (
            <button data-plataforma-boton-menu onClick={() => setAbierto(o => !o)} title="Menú del panel de plataforma"
              style={{ width: '40px', height: '40px', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', flexShrink: 0 }}>
              <Ico n="menu" s={20} />
            </button>
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '15px', fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{actual ? actual.label : 'Panel de plataforma'}</div>
            {!esMovil && <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{actual ? actual.descripcion : 'Clínicas, soporte, consumo y contenido de todas las clínicas'}</div>}
          </div>
          <div style={{ flex: 1 }} />
          {/* El acceso pleno se dice: quien lo ve todo tiene que poder distinguir "soy el dueño" de
              "esto está degradado", que es lo que cuenta el recuadro del pie. */}
          {/* LA AVERIA, EN LA CABECERA: el unico sitio que se ve a las TRES anchuras. El recuadro
              del pie vive dentro del aside, y el aside no esta a la vista ni en la tablet (carril
              de 76 px) ni en el movil (cajon fuera de pantalla hasta que se pulsa el boton), asi
              que en esas dos el aviso existia en el HTML y no lo leia nadie. Medido pintando a
              1400, 900 y 375. Va antes de «Acceso pleno» porque son excluyentes: durante la averia
              el pleno no puede cambiar nada, y decirle que lo tiene todo seria el aviso contrario. */}
          {degradado && (
            <span data-averia-cabecera title="El panel esta en modo averia: solo lectura. El motivo esta en el aviso del menu."
              style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.4px', color: 'var(--warning)', background: 'color-mix(in srgb, var(--warning) 14%, transparent)', border: '1px solid var(--warning)', borderRadius: '6px', padding: '4px 9px', flexShrink: 0 }}>
              <Ico n="warn" s={13} c="var(--warning)" />Solo lectura
            </span>
          )}
          {/* t478 · Y ARRIBA, a las tres anchuras: cuántas consultas de soporte esperan (en rojo si alguna dice que es urgente
              o pide asistencia remota). Lleva a Consultas. */}
          {soporte.n > 0 && (
            <button type="button" data-soporte-pendientes-arriba={soporte.n} onClick={() => navigate('/superadmin/soporte')}
              title={(soporte.n === 1 ? '1 consulta de soporte espera respuesta' : soporte.n + ' consultas de soporte esperan respuesta') + (soporte.urgentes ? ' (' + soporte.urgentes + ' urgente' + (soporte.urgentes === 1 ? '' : 's') + ')' : '')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 800, padding: '6px 11px', borderRadius: '999px', flexShrink: 0,
                background: soporte.urgentes ? 'var(--danger)' : 'color-mix(in srgb, var(--danger) 14%, transparent)', color: soporte.urgentes ? '#fff' : 'var(--danger)' }}>
              <Ico n="chat" s={15} c={soporte.urgentes ? '#fff' : 'var(--danger)'} />{esMovil ? soporte.n : 'Soporte (' + soporte.n + ')'}
            </button>
          )}
          {!esMovil && pleno && !degradado && (
            <span style={{ fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.4px', color: 'var(--primary)', background: 'color-mix(in srgb, var(--primary) 12%, transparent)', borderRadius: '6px', padding: '4px 9px', flexShrink: 0 }}>Acceso pleno</span>
          )}
          <button onClick={() => navigate('/perfil')} style={{ display: 'flex', alignItems: 'center', gap: '10px', border: 'none', background: 'transparent', cursor: 'pointer', padding: '4px', flexShrink: 0 }}>
            <AvatarPorFirmar>
              {user?.avatar
                ? <img src={user.avatar} alt="" style={{ width: '38px', height: '38px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                : <span style={{ width: '38px', height: '38px', borderRadius: '50%', background: 'var(--primary)', color: 'var(--sobre-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '15px', flexShrink: 0 }}>{inicial(user?.nombre)}</span>}
            </AvatarPorFirmar>
            {!esCompacto && <span style={{ textAlign: 'left' }}>
              <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--text-primary)' }}>{nombre}</div>
              <div style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>Equipo de plataforma</div>
            </span>}
          </button>
        </header>

        {/* TAMBIEN EN EL PANEL, Y NO ES REDUNDANTE. Se entra en una clinica DESDE aqui, y
            despues se sigue trabajando aqui un rato (mirar el registro, soltar otro acceso).
            Si el aviso viviera solo en las pantallas de la clinica, el momento entre «he
            entrado» y «me he ido a pacientes» seria justo el que no dice nada. Entre la cabecera y
            el <main>, fuera de lo que se desplaza: a la vista y sin tapar nada (3-oct-2026). */}
        <DentroDeClinica />

        <main data-scrolltop style={{ flex: 1, overflowY: 'auto', padding: esMovil ? '18px 14px calc(18px + var(--sa-abajo))' : '28px 32px calc(28px + var(--sa-abajo))' }}>
          {/* El disco del servidor (ámbar desde el 70 %, rojo desde el 85 %) y el depósito de Spaces: services/discoServidor. */}
          {/* B-1251 · en Inicio, siempre; en las demás, solo cuando avisa (ámbar o rojo). */}
          <AvisoDiscoPerezoso soloSiAvisa={location.pathname !== RUTA_INICIO} />
          <AvisoRelePerezoso />
          {/* Solo en el entorno de pruebas: el visto bueno para pasar a producción a las 04:00 (PasoProduccionPruebas). */}
          <PasoProduccionPerezoso />
          <SegundoFactorPerezoso />
          {/* B-1251 · lo que va DENTRO de esta sección (Precios de WhatsApp en WhatsApp): su botón, o la vuelta. */}
          <BarraDeDentro actual={actual} pathname={location.pathname} ir={navigate} />
          {/* t478 · encima de Consultas, «Tus avisos de soporte» (solo con la sección: la pantalla de debajo ya la exige). */}
          <MisAvisosPerezoso ver={location.pathname === '/superadmin/soporte' && (pleno || (Array.isArray(secciones) && secciones.includes('soporte')))} />
          <ErrorBoundary label="Panel de plataforma"><Outlet /></ErrorBoundary>
        </main>
        {/* B-361 · la IA del equipo: la misma sección que contestar las consultas (el servidor lo vuelve a mirar). */}
        {(pleno || (Array.isArray(secciones) && secciones.includes('soporte'))) && <ConsultaIASoporte />}
        {/* t251 · lo que espera al equipo en el chat de soporte, bien a la vista (la misma sección). */}
        {(pleno || (Array.isArray(secciones) && secciones.includes('soporte'))) && <AvisoSoportePerezoso />}
      </div>
    </div>
  )
}
